import { type KeyboardEvent, useCallback, useMemo } from 'react';
import { Button, FormField, TextInput } from '@direct-frontend/components';
import { useField, useFormContext, useFormInstance } from '@adv-frontend/form-kit';
import { applyOrderIncentive, endUpdateOrderSide, removeOrderIncentive, startUpdateOrderSide } from '@/entities/order';
import { type OrderIncentiveType } from '@/entities/order';
import { trackGoal } from '@/entities/trackGoal';
import { type OrderStatus, OrderStatuses } from '@/shared/api/order';
import { useAsyncFn } from '@/shared/lib/hooks';
import { ORDER_FORM_ID } from '../../constants';
import { orderForm } from '../../model/forms/orderForm';
import styles from './PromoCodeInput.module.css';

const hasCodeField = (error: unknown): error is Record<'code', string> =>
    typeof error === 'object' && error !== null && 'code' in error;

export const usePromoCodeController = () => {
    const { fields } = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });
    const { signature, orderId, storeSlug } = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const fieldPromoCode = useField(fields.promocode);

    const onApplyPromoCodeCallback = useCallback(async () => {
        try {
            startUpdateOrderSide();

            await applyOrderIncentive({
                order_id: orderId,
                code: fieldPromoCode.value.toUpperCase(),
                signature,
                store_slug: storeSlug,
            });
            trackGoal('promocode_apply');

            // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
            // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
            fields.promocodeApply.setValue(fieldPromoCode.value.toUpperCase());
            // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
            // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
            fieldPromoCode.handleChange('');
        } catch (error) {
            if (hasCodeField(error) && error.code === 'NetworkError') {
                throw new Error('Ошибка сети. Проверьте подключение к интернету и повторите попытку.', {
                    cause: error,
                });
            }

            throw error;
        } finally {
            endUpdateOrderSide();
        }
    }, [fieldPromoCode, fields.promocodeApply, orderId, signature, storeSlug]);

    const onRemovePromoCodeCallback = useCallback(
        async (incentiveType: OrderIncentiveType) => {
            try {
                startUpdateOrderSide();

                await removeOrderIncentive({
                    order_id: orderId,
                    signature,
                    store_slug: storeSlug,
                    incentive_type: incentiveType,
                });

                if (incentiveType === 'PROMOCODE') {
                    // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
                    // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
                    fields.promocode.setValue('');
                    // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
                    // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
                    fields.promocodeApply.setValue('');
                }

                if (incentiveType === 'GIFT_CARD') {
                    // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
                    // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
                    fields.giftCardApply.setValue('');
                }
            } catch (error) {
                throw error;
            } finally {
                endUpdateOrderSide();
            }
        },
        [fields.giftCardApply, fields.promocode, fields.promocodeApply, orderId, signature, storeSlug]
    );

    const [{ error: applyError, loading: applyLoading }, applyPromo] = useAsyncFn(onApplyPromoCodeCallback, [
        onApplyPromoCodeCallback,
    ]);

    const [{ error: removePromoError, loading: removePromoLoading }, onRemovePromoCode] = useAsyncFn(
        onRemovePromoCodeCallback,
        [onRemovePromoCodeCallback]
    );

    const onApplyPromoCode = useCallback(() => {
        if (applyLoading) {
            return;
        }

        applyPromo();
    }, [applyPromo, applyLoading]);

    return {
        error: (applyError || removePromoError)?.message,
        loading: applyLoading || removePromoLoading,
        onApplyPromoCode,
        onRemovePromoCode,
        fieldProps: fieldPromoCode.getProps(),
    };
};

interface PromoCodeInputProps {
    //TODO SS-696 Выпилить как бэк поддержит редактирование ордера
    orderStatus: OrderStatus;
}

export const PromoCodeInput = (props: PromoCodeInputProps) => {
    const { orderStatus } = props;
    const controller = usePromoCodeController();

    const addonRight = useMemo(() => {
        if (orderStatus === OrderStatuses.PENDING_PAYMENT) {
            return null;
        }

        if (!controller.fieldProps.value) {
            return null;
        }

        return (
            <Button size="xs" color="accent" onClick={controller.onApplyPromoCode}>
                Применить
            </Button>
        );
    }, [orderStatus, controller.fieldProps.value, controller.onApplyPromoCode]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                controller.onApplyPromoCode();
            }
        },
        [controller]
    );

    return (
        <FormField size="s" errorMessage={controller.error}>
            {(fieldProps) => (
                <TextInput
                    {...fieldProps}
                    {...controller.fieldProps}
                    className={styles.promoField}
                    onKeyDown={onKeyDown}
                    placeholder="Промокод или подарочная карта"
                    isDisabled={controller.loading || orderStatus === OrderStatuses.PENDING_PAYMENT}
                    addonRight={addonRight}
                />
            )}
        </FormField>
    );
};
